<template>
  <div>
    <global-confirm-box ref="confirmBox"></global-confirm-box>

    <page-header
      :title="$t('branches')"
      :subtitle="$t('manage_branches')"
      icon="mdi-office-building"
      icon-color="primary"
    >
      <template #actions>
        <print-button @click="print('print')" class="mr-2"></print-button>

        <primary-button @click="dialog = true">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_firm") }}
        </primary-button>
      </template>
    </page-header>

    <page-toolbar
      :search.sync="search"
      :search-placeholder="$t('search_branches_placeholder')"
      searchable
    />

    <company-dialog
      v-model="dialog"
      :edited-item="editedItem"
      @updated="updateBranch"
      @created="addBranch"
    ></company-dialog>

    <div class="px-6 mt-2">
      <v-data-table
        :headers="headers"
        :items="customSearch(companies, headers, search)"
        class="ordera-box-shadow striped-table"
        id="print"
        show-expand
        :expanded.sync="expanded"
      >
      <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
        <template v-if="item.children && item.children.length">
          <primary-button @click="expand(!isExpanded)" height="32px" color="transparent">
            <span style="text-transform: initial" class="black--text">
              {{ $t(isExpanded ? "hide" : "branches") }}
            </span>
            <v-icon class="ml-2" color="black">
              {{ isExpanded ? "mdi-chevron-up-circle" : "mdi-chevron-down-circle" }}
            </v-icon>
          </primary-button>
        </template>
      </template>

      <template v-slot:expanded-item="{ item, isExpanded, expand }">
        <template v-if="item.children">
          <td :colspan="headers.length" class="pa-0">
            <v-data-table
              :headers="headers"
              class="ordera-box-shadow striped-table"
              :items="item.children || []"
              disable-pagination
              hide-default-footer
              hide-default-header
            >
              <v-spacer></v-spacer>
              <template v-slot:item.address="{ item }">
                {{ toLatin(item.address) }}
              </template>
              <template v-slot:item.actions="{ item, index }">
                <v-btn
                  small
                  color="primary"
                  text
                  :to="{
                    name: 'firma-id-korisnici',
                    params: {
                      id: item.id,
                      name: item.name,
                      parent_id: item.parent_id,
                    },
                  }"
                >
                  {{ $t("korisnici") }} {{ item.users_count }}
                </v-btn>
                <edit-button class="mr-2" @click="editItem(item)"></edit-button>
                <!-- <remove-button @click="deleteItem(item)"></remove-button> -->
              </template>
            </v-data-table>
          </td>
        </template>
      </template>

      <template v-slot:item.address="{ item }">
        {{ toLatin(item.address) }}
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          small
          color="primary"
          text
          class="mx-2"
          :to="{
            name: 'firma-id-korisnici',
            params: { id: item.id, name: item.name, parent_id: item.parent_id },
          }"
          v-if="
            (item.children && item.children.length == 0) ||
            !item.hasOwnProperty('children')
          "
        >
          {{ $t("korisnici") }} {{ item.users_count }}
        </v-btn>
        <v-btn
          small
          color="rgb(251, 109, 59)"
          text
          class="mx-2"
          :to="{
            name: 'firma-id-korisnici',
            params: { id: item.id, name: item.name },
          }"
          v-else
        >
          {{ $t("korisnici") }}
          {{
            item.users_count +
            (item.children || []).reduce((sum, item) => {
              return item.users_count ? sum + item.users_count : sum;
            }, 0)
          }}
        </v-btn>
        <edit-button class="mr-2" @click="editItem(item)"></edit-button>
      </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import hasPrinting from "@/mixins/hasPrinting";
import hasSnackbar from "@/mixins/hasSnackbar";
import { defaultCompany } from "@/models/company";
import CompanyDialog from "@/components/company/dialogs/CompanyDialog.vue";
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import companyService from "@/services/company";

export default {
  mixins: [hasPrinting, hasSnackbar],
  components: {
    CompanyDialog,
    PageHeader,
    PageToolbar,
  },
  data: () => ({
    dialog: false,
    search: "",
    companies: [],
    editedItem: { ...defaultCompany },
    expanded: [], // Track expanded rows
  }),

  created() {
    this.initialize();
  },

  methods: {
    updateBranch(branch) {
      const parentIndex = this.companies.findIndex((i) => i.id === branch.parent_id);
      if (parentIndex > -1) {
        const index = this.companies[parentIndex].children.findIndex(
          (child) => child.id === branch.id
        );
        if (index > -1) {
          this.companies[parentIndex].children.splice(index, 1, branch);
        }
      } else {
        const index = this.companies.findIndex((i) => i.id === branch.id);
        if (index > -1) {
          this.companies.splice(index, 1, branch);
        }

        return;
      }
      this.dialog = false;
    },
    addBranch(branch) {
      const parentIndex = this.companies.findIndex((i) => i.id === branch.parent_id);
      if (parentIndex > -1) {
        this.companies[parentIndex].children.push(branch);
      } else {
        this.companies.push(branch);
      }
      this.dialog = false;
    },
    async initialize() {
      try {
        const res = await companyService.getParentCompanies(this.$store.state.company.id);
        this.companies = res.data;
        if (this.companies.length > 0) {
          this.expanded = [this.companies[0]];
        }
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_branches"));
      }
    },

    editItem(item) {
      if (item.id != this.$store.state.company.id)
        item.parent_id = this.$store.state.company.id;
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },

    deleteItem(item) {
      this.$refs.confirmBox
        .open(this.$t("delete_company"), this.$t("confirm_delete_company"), {
          color: "error",
        })
        .then(() => {
          companyService.deleteCompany(item.id).then(() => {
            const parentIndex = this.companies.findIndex((i) =>
              i.children.some((child) => child.id === item.id)
            );
            if (parentIndex > -1) {
              const index = this.companies[parentIndex].children.findIndex(
                (child) => child.id === item.id
              );
              this.companies[parentIndex].children.splice(index, 1);
            } else {
              const index = this.companies.findIndex((child) => child.id === item.id);
              this.companies.splice(index, 1);
            }
            this.showSuccessSnackbar(this.$t("branch_deleted"));
          })
          .catch(() => {
            this.showErrorSnackbar(this.$t("error_deleting_branch"));
          });
        })
        .catch((err) => console.warn(err));
    },

    print() {
      this.printDocument({
        tables: [
          {
            headers: this.headers,
            items: this.companies,
          },
        ],
        title: this.$t("branches"),
      });
    },
  },
  computed: {
    defaultCompany() {
      const company = this.$store.state.company;
      return {
        ...defaultCompany,
        shift_one: company.shift_one,
        shift_two: company.shift_two,
        shift_three: company.shift_three,
        shift_four: company.shift_four,
        type: company.type,
      };
    },
    headers() {
      return [
        {
          text: "ID",
          align: "end",
          value: "id",
        },
        {
          text: this.$t("title"),
          align: "start",
          value: "name",
        },
        {
          text: this.$t("address"),
          align: "start",
          value: "address",
          sortable: false,
        },
        {
          text: this.$t("email"),
          align: "start",
          value: "email",
          sortable: false,
        },
        {
          text: this.$t("actions"),
          align: "center",
          value: "actions",
          sortable: false,
        },
        {
          text: "",
          value: "data-table-expand",
        },
      ];
    },
  },

  watch: {
    dialog: {
      handler: function (val) {
        if (!val) {
          this.editedItem = Object.assign({}, this.defaultCompany);
        }
      },
      immediate: true,
    },
  },
};
</script>
